import { Box } from '@src/common/components/Box';
import { Stack } from '@src/common/components/Stack';
import { theme } from '@src/common/styles/Theme';
import { useGetPermitFiltersOptions } from '@src/permits/hooks/useGetPermitFiltersOptions';
import { ClaimedByFilter } from '@src/permits/permitSearch/filters/ClaimedByFilter';
import { EffectiveDateFilter } from '@src/permits/permitSearch/filters/EffectiveDateFilter';
import { OrderClientFilter } from '@src/permits/permitSearch/filters/OrderClientFilter';
import { PermitJurisdictionFilter } from '@src/permits/permitSearch/filters/PermitJurisdictionFilter';
import { PermitNameFilter } from '@src/permits/permitSearch/filters/PermitNameFilter';
import { PermitTypeFilter } from '@src/permits/permitSearch/filters/PermitTypeFilter';
import { PONumberFilter } from '@src/permits/permitSearch/filters/PONumberFilter';
import { StateIdFilter } from '@src/permits/permitSearch/filters/StateIdFilter';
import { StatusFilter } from '@src/permits/permitSearch/filters/StatusFilter';
import { SubmissionDateFilter } from '@src/permits/permitSearch/filters/SubmissionDateFilter';
import { TractorLicensePlateNumberFilter } from '@src/permits/permitSearch/filters/TractorLicensePlateNumberFilter';
import { TractorUnitNumberFilter } from '@src/permits/permitSearch/filters/TractorUnitNumberFilter';
import type { SearchPermitBasicFilters } from '@src/permits/services/PermitApplicationClient';

interface Props {
  filters: SearchPermitBasicFilters;
  onFilterChange: (filters: SearchPermitBasicFilters) => void;
}

export const PermitFilters = ({ onFilterChange, filters }: Props) => {
  const { options } = useGetPermitFiltersOptions();
  if (!filters) {
    return null;
  }
  const selectedClientOptions = (filters.client || [])
    .map((clientId) => {
      const client = options?.clients.find((c) => c.id === clientId);
      return client
        ? {
            name: client.name,
            value: client.id,
          }
        : null;
    })
    .filter(
      (option): option is { name: string; value: string } => option !== null,
    );

  const width = '300px';

  return (
    <Stack gap={theme.spacing['5']}>
      <Box
        gap={theme.spacing['5']}
        sx={{ display: 'flex', flexWrap: 'wrap', flexDirection: 'row' }}
      >
        <Stack width={width}>
          <PermitTypeFilter
            onFilterChange={onFilterChange}
            value={filters.type}
          />
        </Stack>

        <Stack width={width}>
          <PermitNameFilter
            onFilterChange={onFilterChange}
            value={filters.permitName}
          />
        </Stack>

        <Stack width={width}>
          <PermitJurisdictionFilter
            onFilterChange={onFilterChange}
            value={filters.jurisdiction}
            options={options?.jurisdictions}
          />
        </Stack>

        <Stack width={width}>
          <StateIdFilter
            onFilterChange={onFilterChange}
            value={filters.stateId}
          />
        </Stack>

        <Stack width={width}>
          <StatusFilter
            onFilterChange={onFilterChange}
            value={filters.status}
            options={options?.statuses}
          />
        </Stack>

        <Stack width={width}>
          <EffectiveDateFilter
            onFilterChange={onFilterChange}
            value={filters.effectiveDate}
          />
        </Stack>
        <Stack width={width}>
          <SubmissionDateFilter
            onFilterChange={onFilterChange}
            value={filters.submissionDate}
          />
        </Stack>
        <Stack width={width}>
          <TractorUnitNumberFilter
            onFilterChange={onFilterChange}
            value={filters.tractorUnitNumber}
          />
        </Stack>

        <Stack width={width}>
          <TractorLicensePlateNumberFilter
            onFilterChange={onFilterChange}
            value={filters.tractorLicensePlateNumber}
          />
        </Stack>

        <Stack width={width}>
          <PONumberFilter
            onFilterChange={onFilterChange}
            value={filters.poNumber}
          />
        </Stack>

        <Stack width={width}>
          <OrderClientFilter
            onFilterChange={onFilterChange}
            value={selectedClientOptions}
            options={options?.clients}
          />
        </Stack>
        <Stack width={width}>
          <ClaimedByFilter
            onFilterChange={onFilterChange}
            value={filters.claimedBy}
          />
        </Stack>
      </Box>
    </Stack>
  );
};
